import React from 'react';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import {
	useLazyDoctorBasicInfoQuery,
	useLazyRootWorkplaceDoctorsQuery,
	useLazyWorkplaceDoctorsQuery,
	useWorkplacesQuery,
} from '../../../../graphql/graphql';
import { useAppContext } from '../../../../Services/AppContext';
import { FieldError } from 'react-hook-form';
import DynamicSelect from '../../../../Components/Base/DynamicSelect/DynamicSelect';
import { useEffectAsync } from '../../../../helpers';
import { useMemo } from 'react';
import useWorkplaces from '../../../Workplace/Hooks/useWorkplaces';

interface DoctorSelectProps {
	onChange?: (value: SelectValue) => void;
	workplaceId?: string;
	value?: SelectValue;
	error?: FieldError;
	placeholder?: string;
	searchInAllWorkplaces?: boolean;
	roleKeys?: string[];
	approved?: boolean;
	loadFromRoot?: boolean;
}

const DoctorSelect: React.FC<DoctorSelectProps> = ({
	workplaceId,
	value,
	onChange,
	searchInAllWorkplaces = false,
	roleKeys,
	approved,
	loadFromRoot = false,
	...props
}) => {
	const [searchDoctors] = useLazyWorkplaceDoctorsQuery();
	const [getDoctorInfo] = useLazyDoctorBasicInfoQuery();
	const [searchRootDoctors] = useLazyRootWorkplaceDoctorsQuery();
	const { userData } = useAppContext();
	const { workplaceIds } = useWorkplaces();

	let currentWorkplaceId: string;
	if (!workplaceId) {
		currentWorkplaceId = userData.workplaceId;
	} else {
		currentWorkplaceId = workplaceId;
	}

	useEffectAsync(async () => {
		if (value?.value && !value?.label) {
			const { user } = await getDoctorInfo({
				id: value?.value,
			}).unwrap();

			if (user?.profile?.fullName) {
				const newValue = { value: user.id, label: user.profile.fullName };

				if (onChange) {
					onChange(newValue);
				}
			}
		}
	}, [value?.value]);

	const searchDoctorsAtWorkplace = async (searchInput: string) => {
		const { workplaceDoctors } = await searchDoctors({
			fulltext: searchInput,
			roleKeys: roleKeys ?? ['doctor', 'therapist'],
			workplaceIds: searchInAllWorkplaces ? workplaceIds : [currentWorkplaceId],
			approved: approved,
		}).unwrap();

		return workplaceDoctors?.data;
	};

	const searchDoctorsAtRoot = async (searchInput: string) => {
		const { rootWorkplaceDoctors } = await searchRootDoctors({
			fulltext: searchInput,
		}).unwrap();

		return rootWorkplaceDoctors?.data;
	};

	const handleSearchItem = async (
		searchInput: string,
	): Promise<SelectValue[]> => {
		console.log('loadFromRoot', loadFromRoot);
		const data = loadFromRoot
			? await searchDoctorsAtRoot(searchInput)
			: await searchDoctorsAtWorkplace(searchInput);

		const items: SelectValue[] = [];

		for (const workplaceDoctor of data || []) {
			if (workplaceDoctor && workplaceDoctor.user) {
				if (!items.some(item => item.value === workplaceDoctor?.user?.id)) {
					items.push({
						value: workplaceDoctor.user.id,
						label: workplaceDoctor.user.profile?.fullName || '',
					});
				}
			}
		}

		return items;
	};

	return (
		<DynamicSelect
			searchItems={handleSearchItem}
			{...props}
			onChange={onChange}
			value={value}
		/>
	);
};

export default DoctorSelect;
